import PropTypes from "prop-types";
import styled from "styled-components";
import { useField } from "formik";
import chevronDown from "assets/icons/chevronDown.svg";
import { useClickOutside } from "webrix/hooks";

export const DropdownWrapper = styled.div`
  width: 100%;
  position: relative;

  .selectInput {
    display: block;
    color: var(--grey_1);
    width: 80%;
    background-color: transparent;
    border: none;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0px;
    pointer-events: none;

    &::placeholder {
      color: ${(props) =>
        props.disabled ? "var(--off_white)" : "var(--grey_9)"};
    }
  }

  .inputWrapper {
    position: relative;
    width: 100%;
    height: 48px;
    border: 1px solid var(--grey_10);
    border-radius: 8px;
    padding: 0 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: var(--sup_text);
    background-color: var(--white);
    cursor: pointer;

    &.error {
      border-color: var(--danger);
    }

    &.hasValue {
      border-color: var(--secondary);
      color: var(--secondary);
    }
  }

  .toggleIcon {
    position: absolute;
    right: 12px;
    height: 20px;
    transition: var(--transition_default);
    cursor: pointer;
  }

  .list {
    width: 100%;
    max-height: 316px;
    overflow: auto;
    position: absolute;
    top: 4.8rem;
    left: 0;
    background-color: var(--white);
    border-radius: 1rem;
    box-shadow: 0px 25px 35px 0px #00000008;
    display: none;
    opacity: 0;
    pointer-events: none;
    transition: var(--transition_default);
    z-index: 5;
  }

  .listItem {
    width: 100%;
    text-transform: capitalize;
    height: 48px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    color: var(--grey_1);

    &:hover {
      background-color: var(--background);
    }
  }

  .active-item {
    background-color: #f7fafd;
    color: var(--secondary);
  }

  .filter-list-item {
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover {
      background-color: var(--background);
    }
  }

  &.isOpen {
    .toggleIcon {
      transform: rotateZ(180deg);
    }

    .list {
      top: 90px;
      opacity: 1;
      pointer-events: all;
      display: block;
    }

    &.isOpen.no-label .list {
      top: 60px;
    }
  }

  &.hasIcon {
    .header {
      justify-content: flex-start;

      .title {
        margin-left: 0.8rem;
      }
    }
  }

  .iconLeft {
    margin-right: 0.8rem;
  }

  label {
    display: block;
    color: var(--body_text);
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    margin-bottom: 0.3rem;

    sup {
      color: var(--danger);
    }
  }

  .clearState {
    padding: 1rem;
  }
`;

/**
 * Dropdown component.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The class name for the component.
 * @param {string} props.name - The name of the dropdown.
 * @param {string} props.type - The type of the input element.
 * @param {string} [props.label] - The label for the dropdown.
 * @param {string} [props.placeholder="Please select"] - The placeholder text for the dropdown.
 * @param {Array} props.list - The list of options for the dropdown.
 * @param {boolean} [props.readOnly=false] - Whether the dropdown is read-only.
 * @param {boolean} [props.required=true] - Whether the dropdown is required.
 * @returns {JSX.Element} The rendered Dropdown component.
 */
const Dropdown = ({
  className,
  name,
  type,
  label,
  placeholder = "Please select",
  list,
  readOnly,
  required = true,
}) => {
  const [field, meta, helpers] = useField(name);
  const { value } = meta;
  const { setValue } = helpers;

  const toggleList = (open) => {
    if (readOnly) return;

    open
      ? document.querySelector(`#${name}`).classList.add("isOpen")
      : document.querySelector(`#${name}`).classList.remove("isOpen");
  };

  const handleSelect = (e, l) => {
    e.preventDefault();
    e.stopPropagation();
    setValue(l);
    toggleList(false);
  };

  const handleMouseDownCapture = useClickOutside(() =>
    document.querySelector(`#${name}`).classList.remove("isOpen")
  );

  return (
    <DropdownWrapper id={name} className={`dWrapper ${className ?? ""}`}>
      <label htmlFor={name} className="label">
        {label}
        {required && <sup>*</sup>}
      </label>
      <div
        className={`inputWrapper${meta.touched && meta.error ? " error" : ""}`}
        onClick={() =>
          document.querySelector(`#${name}`).classList.toggle("isOpen")
        }
        onMouseDownCapture={handleMouseDownCapture}
      >
        <input
          type={type || "text"}
          name={name}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          placeholder={placeholder}
          className="selectInput"
          readOnly
          {...field}
        />
        <img src={chevronDown} alt="down" className="toggleIcon" />
      </div>
      {meta.touched && meta.error && (
        <span className="l3 colorDanger">{meta.error}</span>
      )}
      <div className="list">
        {!!list?.length &&
          list.map((item) => (
            <button
              key={item}
              className={`listItem l3 colorBodyText ${
                item === value && "active-item"
              }`}
              onClick={(e) => handleSelect(e, item.toString())}
            >
              {item}
            </button>
          ))}
      </div>
    </DropdownWrapper>
  );
};

Dropdown.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  // value: PropTypes.string.isRequired,
  // setValue: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  list: PropTypes.array,
  hasIcon: PropTypes.bool,
  icon: PropTypes.any,
  readOnly: PropTypes.bool,
};

export default Dropdown;
